import { useState, useCallback, memo } from 'react'
import { MdVpnKey } from 'react-icons/md'
import * as Yup from 'yup'
import { useHistory } from 'react-router-dom'

import Button from 'shared/components/Button'
import Modal from 'shared/components/Modal'
import Text from 'shared/components/Text'
import Input from 'shared/components/Input'
import { colors } from 'shared/styles/colors'
import { encrypt } from 'shared/utils/crypt'
import SnackBar from 'shared/components/SnackBar'
import { IVerifyPassword } from 'modules/pagamentos/domain/usecases/VerifyPassword'
import IConsultaCartoesResponseDTO from 'modules/pagamentos/domain/entities/IConsultaCartoesResponseDTO'
import { useFrotista } from 'modules/user/hooks/useFrotista'

import { Container } from '../../Pagamento/styles'
import {
  ContainerIcon,
  StyledForm,
  ContainerContent,
  FormWrapper,
  ContainerText,
} from './styles'
import { FormikHelpers } from 'formik/dist/types'

interface IModalSenhaProps {
  openModalPassword: boolean
  setOpenModalPassword(state: boolean): void
  verifyPassword: IVerifyPassword
  card: IConsultaCartoesResponseDTO
  setCartao(cartao: IConsultaCartoesResponseDTO): void
  setActiveMenu(menu: string): void
  curvature?: boolean
  customPasswordValidation?: (password: string) => void
}

interface IFormProps {
  password: string
}

const ModalSenha = ({
  openModalPassword,
  setOpenModalPassword,
  verifyPassword,
  card,
  setCartao,
  setActiveMenu,
  curvature,
  customPasswordValidation,
}: IModalSenhaProps) => {
  const [loading, setLoading] = useState(false)
  const { frotistaId } = useFrotista()
  const [openSnack, setOpenSnack] = useState(false)
  const [message, setMessage] = useState('')
  const history = useHistory()

  const handleOkButton = useCallback(
    async (values: IFormProps, formikHelpers: FormikHelpers<IFormProps>) => {
      setLoading(true)

      try {
        if (customPasswordValidation) {
          customPasswordValidation(values.password)
          setLoading(false)
          setOpenModalPassword(false)
        } else {
          const data = encrypt(
            {
              senha: values.password,
              identificador: card.identificador,
            },
            false,
            frotistaId.toString(),
          )
          await verifyPassword.execute({ password: data, frotistaId })

          setCartao(card)
          setLoading(false)
          setOpenModalPassword(false)
          setActiveMenu('extrato')
        }
        formikHelpers.resetForm()
      } catch (e: unknown) {
        setOpenSnack(true)
        setLoading(false)
        if (e instanceof Error) {
          setMessage(e.message)
        } else {
          setMessage('Erro ao validar senha')
        }
      }
    },
    [
      setCartao,
      card,
      setOpenModalPassword,
      setActiveMenu,
      frotistaId,
      verifyPassword,
    ],
  )

  const handleCloseModalButton = useCallback(() => {
    if (customPasswordValidation) {
      setOpenModalPassword(false)
    } else {
      history.push('/lotes')
    }
  }, [card, frotistaId, customPasswordValidation])

  return (
    <>
      <Modal
        openModal={openModalPassword}
        size="small"
        showCloseButton={true}
        onCloseModal={handleCloseModalButton}
        curvature={curvature}
      >
        <Container>
          <ContainerIcon>
            <MdVpnKey />
          </ContainerIcon>

          <ContainerContent>
            <Text size={24} weight="700">
              Digite sua senha TMOV Pay
            </Text>

            <StyledForm
              enableReinitialize
              onSubmit={(values, formikHelpers) =>
                handleOkButton(
                  values as IFormProps,
                  formikHelpers as FormikHelpers<IFormProps>,
                )
              }
              initialValues={{
                password: '',
              }}
              validationSchema={() => ({
                password: Yup.string().min(
                  4,
                  'A senha deve conter 4 caracteres',
                ),
              })}
            >
              {formControl => (
                <FormWrapper>
                  <Input
                    placeholder="****"
                    name="password"
                    type="password"
                    maxLength={4}
                    formControl={formControl}
                  />
                  <ContainerText>
                    <Text color={colors.black}>
                      Esqueceu ou não sabe a senha do Tmov Pay? Ligue para{' '}
                      <b>0800 701 3526</b>
                    </Text>
                  </ContainerText>
                  <Button
                    color={colors.green}
                    type="submit"
                    onClick={formControl.handleSubmit}
                    disabled={loading}
                    loading={loading}
                  >
                    Confirmar
                  </Button>
                </FormWrapper>
              )}
            </StyledForm>
          </ContainerContent>
        </Container>
      </Modal>
      <SnackBar
        open={openSnack}
        message={message}
        setOpen={setOpenSnack}
        severity={'error'}
      />
    </>
  )
}

export default memo(ModalSenha)
